import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ComposedChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LabelList,
  ResponsiveContainer,
} from 'recharts';
import { Card, CardHeader, CardContent } from '@cortex/web-design-system/components/ui/Card';
import { WeeklyPointTable } from './WeeklyPointTable.js';
import { DevelopPointViewSwitch } from './DevelopPointViewSwitch.js';
import {
  DEFAULT_DEVELOP_POINT_VIEW,
  supportsRequirementView,
  type DevelopPointView,
} from './develop-point-view.js';
import {
  renderRequirementSegment,
  requirementViewChartFields,
  weeklySeriesLabelKey,
  renderWeeklyTargetLines,
} from './weekly-requirement-series.js';
import { formatPt } from '../../../lib/format.js';
import {
  BAR_MAX_SIZE,
  BAR_RADIUS_TOP,
  stackedBarRadius,
  createCompactLegendContent,
  TEAM_BREAKDOWN_ORDER,
} from './chart-styles.js';
import { isTetSubTeam } from '@cortex/domain-product-innovation-lab-types';
import type {
  KpiWeeklyTrendPoint,
  KpiWeeklyTrendBreakdownKey,
  KpiTeam,
} from '../../../types/api.js';

/**
 * 週次pt集計のデフォルト範囲を返す: 「今日」を含む週の月曜の 8週前 〜 4週後の日曜。
 * 呼び出し毎に `new Date()` を読み直すため、日またぎでアプリを開きっぱなしでも
 * 自動更新される（override が無い場合のみ反映）。
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business 週次pt集計チャートの初期表示範囲（8週前の月曜〜4週後の日曜）を生成
 * @graph-connects none
 */
export function computeDefaultWeeklyDateRange(): { start: string; end: string } {
  const today = new Date();
  const dow = today.getDay(); // 0=Sun, 1=Mon, ..., 6=Sat
  const mondayDiff = dow === 0 ? -6 : 1 - dow;
  const monday = new Date(today);
  monday.setDate(monday.getDate() + mondayDiff);
  const start = new Date(monday);
  start.setDate(start.getDate() - 7 * 8);
  const end = new Date(monday);
  end.setDate(end.getDate() + 7 * 4 + 6); // +4週の日曜
  const fmt = (d: Date) =>
    `${String(d.getFullYear())}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return { start: fmt(start), end: fmt(end) };
}

interface WeeklyPointTrendChartProps {
  /** 週次pt集計データ */
  data: KpiWeeklyTrendPoint[];
  /** ローディング状態 */
  isLoading: boolean;
  /** 範囲開始日 (YYYY-MM-DD) */
  startDate: string;
  /** 範囲終了日 (YYYY-MM-DD) */
  endDate: string;
  /** 範囲変更コールバック */
  onDateRangeChange: (startDate: string, endDate: string) => void;
  /** 週クリックコールバック（担当者別pt内訳モーダルを開く用） */
  onWeekClick?: (weekStart: string, weekEnd: string) => void;
  /** 親のチームフィルタ。実績pt の内訳ラベル選択に使用 (all=未指定) */
  team?: KpiTeam | 'all';
  /**
   * 開発pt の口径（全部 / 要件定義抜き / 内訳）。開発pt (MTD) カードと共有する場合に渡す。
   * 未指定ならカード内で状態を持つ（既定は要件定義抜き）
   */
  view?: DevelopPointView;
  /** 口径変更コールバック（view と組で渡す） */
  onViewChange?: (view: DevelopPointView) => void;
}

/**
 * 実績pt 内訳キー → 色 のマッピング。recharts の Bar fill に使用
 *  - PDG=#FFB33F / ACE=#C00707 (scope='all' トップグループ)
 *  - ACE 内訳: airCloset=#CB3030, Platform=#40CEEE, Mall=#00EAB7, QA=#FFE97B
 *  - PDG 内訳: PDG=#FFB33F, SRE=#4B6070, TE=#EDC818
 *  - 'ace' キーは scope='all' の時は ACE グループ(#C00707)、scope='ace' の時は ACE 未割当(灰)
 *    を意味する。後者は getBreakdownColor で上書きする
 * @graph-connects none
 */
const BREAKDOWN_COLORS: Record<KpiWeeklyTrendBreakdownKey, string> = {
  pdg: '#FFB33F',
  ace: '#C00707',
  sre: '#4B6070',
  tet: '#EDC818',
  aircloset: '#CB3030',
  platform: '#40CEEE',
  mall: '#00EAB7',
  qa: '#FFE97B',
};

/** ACE 未割当 (team='ace' 内訳の 'ace' キー) 用のニュートラル色 @graph-connects none */
const ACE_REST_COLOR = '#9ca3af';

/**
 * 親 team フィルタを考慮して内訳キーの fill 色を解決
 *  - team='ace' の 'ace' は ACE 未割当 (aceRest) なので灰色
 * @graph-connects none
 */
function getBreakdownColor(
  key: KpiWeeklyTrendBreakdownKey,
  parentTeam: KpiTeam | 'all' | undefined,
): string {
  if (parentTeam === 'ace' && key === 'ace') return ACE_REST_COLOR;
  return BREAKDOWN_COLORS[key];
}

/**
 * 親 team フィルタを踏まえて内訳キーを表示用 i18n key に解決
 *  - team='pdg' の 'pdg' キーは「PDG (TE除く)」と読みたいので pdgRest を返す
 *  - team='ace' の 'ace' キーは「ACE (未割当)」と読みたいので aceRest を返す
 * @graph-connects none
 */
function getBreakdownI18nKey(
  key: KpiWeeklyTrendBreakdownKey,
  parentTeam: KpiTeam | 'all' | undefined,
): string {
  if (parentTeam === 'pdg' && key === 'pdg') return 'kpi.weeklyActualBreakdown.pdgRest';
  if (parentTeam === 'ace' && key === 'ace') return 'kpi.weeklyActualBreakdown.aceRest';
  return `kpi.weeklyActualBreakdown.${key}`;
}

interface WeekChartClickState {
  activeLabel?: string | number;
}

/** weekStart (YYYY-MM-DD, 月曜) → "M/D" 表示 @graph-connects none */
function formatWeekLabel(weekStart: string): string {
  const parts = weekStart.split('-');
  if (parts.length !== 3) return weekStart;
  return `${String(Number(parts[1]))}/${String(Number(parts[2]))}`;
}

/**
 * 週次pt集計チャートカード
 *  - Bar: 週次の開発pt 実績、Line: 週次の開発pt 目標
 *  - Tooltip で達成率と月内累計を併記
 *  - グラフ下に同データの転置表（週が列、メトリクスが行）も併記
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 週次pt集計の可視化カード。週単位の開発pt 目標(fibo) vs 個人積み上げ(戦闘力capacity) vs 実績を Bar+Line の複合チャートで表示し、月内累計と達成率（fibo/個人積み上げの両方）を Tooltip で併記。グラフ下に同データの転置表（週が列、実績pt/処理中pt/実績±処理中増減pt/目標pt/目標pt(個人積み上げ)/達成率/達成率(個人積み上げ)/月内累計実績/月内累計実績(処理中含)/月内累計目標/月内累計目標(個人積み上げ)/累計達成率 の12行）を併記。チャートは hasData 判定（actual>0 or target!=null or targetCombatPower!=null）でガードし、表は data 行があれば常に表示
 * @graph-connects Card [calls] カードコンポーネント描画
 * @graph-connects CardHeader [calls] カードヘッダー描画
 * @graph-connects CardContent [calls] カードコンテンツ描画
 * @graph-connects Select [calls] 表示週数セレクト描画
 * @graph-connects DevelopPointViewSwitch [calls] 開発pt の口径切替（全部 / 要件定義抜き / 内訳）。開発pt (MTD) カードと状態共有
 * @graph-connects renderRequirementSegment [calls] 内訳ビューで実績棒に積む要件定義pt 棒
 * @graph-connects renderWeeklyTargetLines [calls] 目標線（予算・内訳ビューの要件定義・全部ビューの個人積み上げ）
 */
export function WeeklyPointTrendChart({
  data,
  isLoading,
  startDate,
  endDate,
  onDateRangeChange,
  onWeekClick,
  team,
  view: controlledView,
  onViewChange,
}: WeeklyPointTrendChartProps) {
  const { t } = useTranslation();
  const [localView, setLocalView] = useState<DevelopPointView>(DEFAULT_DEVELOP_POINT_VIEW);
  const canSwitchView = supportsRequirementView(team);
  // 要件定義ビュー非対応スコープ（ACE 系）は従来表示（全部）に固定する
  const effectiveView: DevelopPointView = canSwitchView ? (controlledView ?? localView) : 'all';
  const isRequirementView = effectiveView !== 'all';
  const isBreakdownView = effectiveView === 'breakdown';

  const handleChartClick = (state: WeekChartClickState) => {
    if (!onWeekClick) return;
    const weekStart = state.activeLabel;
    if (weekStart == null || typeof weekStart !== 'string') return;
    const row = data.find((d) => d.weekStart === weekStart);
    if (row) {
      onWeekClick(row.weekStart, row.weekEnd);
    }
  };

  const handleBarClick = (data: { weekStart?: string; weekEnd?: string }) => {
    if (!onWeekClick) return;
    if (data.weekStart && data.weekEnd) {
      onWeekClick(data.weekStart, data.weekEnd);
    }
  };

  const breakdownKeys = useMemo<KpiWeeklyTrendBreakdownKey[]>(() => {
    const seen = new Set<KpiWeeklyTrendBreakdownKey>();
    for (const row of data) {
      if (row.developActualByTeam) {
        for (const entry of row.developActualByTeam) {
          seen.add(entry.team);
        }
      }
      // 最新週で「実績ゼロだが処理中ptがある」チームの内訳も bar に出せるようキーへ含める
      if (row.inProgressByTeam) {
        for (const entry of row.inProgressByTeam) {
          seen.add(entry.team);
        }
      }
    }
    return TEAM_BREAKDOWN_ORDER.filter((k) => seen.has(k));
  }, [data]);

  const hasBreakdown = breakdownKeys.length > 0;

  // 「最新週（today を含む現在週）」判定用の今日 (JST)。最新週のみ bar に処理中pt を含める
  const todayJst = useMemo(
    () => new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Tokyo' }).format(new Date()),
    [],
  );

  const chartData = useMemo(
    () =>
      data.map((row) => {
        // 最新週のみ各チームの実績に処理中ptを上乗せして「実績(処理中含)」を bar に表示
        const isCurrentWeek = row.weekStart <= todayJst && todayJst <= row.weekEnd;
        const wipByTeam: Partial<Record<KpiWeeklyTrendBreakdownKey, number>> = {};
        if (isCurrentWeek && row.inProgressByTeam) {
          for (const entry of row.inProgressByTeam) {
            wipByTeam[entry.team] = entry.actual;
          }
        }
        const byTeam: Record<string, number> = {};
        // 要件定義抜き / 内訳ビューは各チームから要件定義分を差し引いた内訳で積み上げる
        const teamActuals = isRequirementView
          ? row.developActualByTeamExcludingRequirement
          : row.developActualByTeam;
        if (teamActuals) {
          for (const entry of teamActuals) {
            byTeam[`byTeam_${entry.team}`] = entry.actual + (wipByTeam[entry.team] ?? 0);
          }
        }
        // Fill missing keys so recharts stacks correctly across weeks。最新週で実績が無くても
        // 処理中ptがあるチームはその値で埋める
        for (const key of breakdownKeys) {
          const dk = `byTeam_${key}`;
          byTeam[dk] ??= wipByTeam[key] ?? 0;
        }
        // Tooltip で「実績（処理中: WIP）」の内訳を出せるよう、チーム別の処理中pt を別フィールドで保持
        const wipFields: Record<string, number> = {};
        for (const key of breakdownKeys) {
          wipFields[`wipByTeam_${key}`] = wipByTeam[key] ?? 0;
        }
        const actual = row.developActual ?? 0;
        const wipTotal = isCurrentWeek ? (row.inProgressPt ?? 0) : 0;
        const base = {
          weekStart: row.weekStart,
          weekEnd: row.weekEnd,
          actual,
          // bar の高さ・ラベル用。最新週は実績＋処理中pt、過去週・未来週は実績のみ
          actualWithWip: actual + wipTotal,
          // Tooltip で単色バー（leaf team）の処理中pt 内訳を出す用
          wip: wipTotal,
          isCurrentWeek,
          target: row.developTarget,
          targetCombatPower: row.developTargetCombatPower,
          // tooltip の月内累計実績は従来どおり「処理中pt 含み」の実態進捗を表示する
          cumulativeActual: row.cumulativeDevelopActualWithWip,
          cumulativeTarget: row.cumulativeDevelopTarget,
          cumulativeTargetCombatPower: row.cumulativeDevelopTargetCombatPower,
          achievementRate: row.achievementRate,
          achievementRateCombatPower: row.achievementRateCombatPower,
          ...byTeam,
          ...wipFields,
        };
        // 要件定義抜き / 内訳ビューは実績・目標を要件定義を除いた値に差し替える
        return isRequirementView ? { ...base, ...requirementViewChartFields(row, wipTotal) } : base;
      }),
    [data, breakdownKeys, todayJst, isRequirementView],
  );

  const hasData =
    chartData.length > 0 &&
    chartData.some((d) => d.actual > 0 || d.target !== null || d.targetCombatPower !== null);

  return (
    <Card className="animate-content-reveal">
      <CardHeader
        title={t('kpi.weeklyPointTrend.title')}
        description={t('kpi.weeklyPointTrend.description')}
        action={
          canSwitchView ? (
            <DevelopPointViewSwitch view={effectiveView} onChange={onViewChange ?? setLocalView} />
          ) : undefined
        }
      />
      <CardContent>
        <div
          className="flex flex-wrap items-center justify-end gap-2 mb-3"
          data-testid="date-range-controls"
        >
          <label className="flex items-center gap-1 text-xs text-[var(--color-text-secondary)]">
            <span>{t('kpi.weeklyPointTrend.rangeStart')}</span>
            <input
              type="date"
              value={startDate}
              onChange={(e) => {
                onDateRangeChange(e.target.value, endDate);
              }}
              className="px-2 py-1 text-sm rounded border border-[var(--color-border)] bg-[var(--color-bg-elevated)] text-[var(--color-text-primary)]"
              aria-label={t('kpi.weeklyPointTrend.rangeStart')}
              data-testid="weekly-trend-start-date"
            />
          </label>
          <label className="flex items-center gap-1 text-xs text-[var(--color-text-secondary)]">
            <span>{t('kpi.weeklyPointTrend.rangeEnd')}</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => {
                onDateRangeChange(startDate, e.target.value);
              }}
              className="px-2 py-1 text-sm rounded border border-[var(--color-border)] bg-[var(--color-bg-elevated)] text-[var(--color-text-primary)]"
              aria-label={t('kpi.weeklyPointTrend.rangeEnd')}
              data-testid="weekly-trend-end-date"
            />
          </label>
        </div>
        {isLoading ? (
          <div className="h-56 rounded-lg bg-[var(--color-bg-elevated)] animate-pulse" />
        ) : hasData ? (
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={chartData}
                margin={{ top: 20, right: 20, left: 10, bottom: 5 }}
                onClick={onWeekClick ? (handleChartClick as never) : undefined}
                style={onWeekClick ? { cursor: 'pointer' } : undefined}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis
                  dataKey="weekStart"
                  stroke="var(--color-text-muted)"
                  fontSize={11}
                  tickFormatter={formatWeekLabel}
                />
                <YAxis
                  stroke="var(--color-text-muted)"
                  fontSize={11}
                  tickFormatter={(value: number) => String(value)}
                />
                <Tooltip
                  wrapperStyle={{ zIndex: 50, outline: 'none' }}
                  contentStyle={{
                    backgroundColor: 'var(--color-bg-elevated)',
                    border: '1px solid var(--color-border)',
                    borderRadius: '8px',
                    boxShadow: 'var(--shadow-lg)',
                  }}
                  labelStyle={{ color: 'var(--color-text-primary)' }}
                  formatter={
                    ((value: number, name: string, item: { payload?: unknown }) => {
                      const numeric = typeof value === 'number' ? value : 0;
                      const payload = item.payload as Record<string, unknown> | undefined;
                      // 最新週は処理中pt を「実績（処理中: WIP）」の括弧付き内訳で表示
                      const withInProgress = (displayValue: number, wip: number, label: string) =>
                        wip > 0
                          ? [
                              `${formatPt(displayValue - wip)}pt（${t('kpi.weeklyInProgressShort')}: ${formatPt(wip)}pt）`,
                              label,
                            ]
                          : [`${formatPt(displayValue)}pt`, label];
                      const seriesLabelKey = weeklySeriesLabelKey(name, effectiveView);
                      if (name === 'actual' || name === 'actualWithWip') {
                        // 単色バー（leaf team）: 最新週は actualWithWip に処理中pt を内包
                        const wip = name === 'actualWithWip' ? Number(payload?.wip ?? 0) : 0;
                        return withInProgress(numeric, wip, t(seriesLabelKey ?? ''));
                      }
                      if (seriesLabelKey) {
                        return [`${formatPt(numeric)}pt`, t(seriesLabelKey)];
                      }
                      if (name.startsWith('byTeam_')) {
                        const teamKey = name.slice('byTeam_'.length) as KpiWeeklyTrendBreakdownKey;
                        // スタックバー: 最新週はそのチームの処理中pt を括弧で内訳表示
                        const wip = Number(payload?.[`wipByTeam_${teamKey}`] ?? 0);
                        return withInProgress(numeric, wip, t(getBreakdownI18nKey(teamKey, team)));
                      }
                      return [String(numeric), name];
                    }) as never
                  }
                  labelFormatter={
                    ((label: string, payload: { payload?: unknown }[]) => {
                      const row = payload[0]?.payload as
                        | {
                            weekEnd?: string;
                            achievementRate?: number | null;
                            achievementRateCombatPower?: number | null;
                            cumulativeActual?: number | null;
                            cumulativeTarget?: number | null;
                          }
                        | undefined;
                      const start = formatWeekLabel(label);
                      const end = row?.weekEnd ? formatWeekLabel(row.weekEnd) : '';
                      const rateLabel = isRequirementView
                        ? t('kpi.achievementRateExcludingRequirement')
                        : t('kpi.achievementRate');
                      const rateCpLabel = isRequirementView
                        ? t('kpi.achievementRateCombatPowerExcludingRequirement')
                        : t('kpi.achievementRateCombatPower');
                      const rate =
                        row?.achievementRate !== undefined && row.achievementRate !== null
                          ? `（${rateLabel}: ${String(row.achievementRate)}%）`
                          : '';
                      const rateCp =
                        row?.achievementRateCombatPower !== undefined &&
                        row.achievementRateCombatPower !== null
                          ? `（${rateCpLabel}: ${String(row.achievementRateCombatPower)}%）`
                          : '';
                      const cum =
                        row?.cumulativeActual != null || row?.cumulativeTarget != null
                          ? ` / ${t('kpi.cumulative')}: ${formatPt(row.cumulativeActual ?? 0)} / ${formatPt(row.cumulativeTarget ?? 0)}`
                          : '';
                      return `${start} - ${end}${rate}${rateCp}${cum}`;
                    }) as never
                  }
                />
                <Legend
                  content={createCompactLegendContent(
                    (value) => {
                      const seriesLabelKey = weeklySeriesLabelKey(value, effectiveView);
                      if (seriesLabelKey) return t(seriesLabelKey);
                      if (value.startsWith('byTeam_')) {
                        const teamKey = value.slice('byTeam_'.length) as KpiWeeklyTrendBreakdownKey;
                        return t(getBreakdownI18nKey(teamKey, team));
                      }
                      return value;
                    },
                    {
                      // ACE 未割当 (team='ace' の 'ace' 内訳) と
                      // PDG (TE/SRE除く) (team='pdg' の 'pdg' 内訳) は凡例に出さない
                      filter: (value) => {
                        if (team === 'ace' && value === 'byTeam_ace') return false;
                        if (team === 'pdg' && value === 'byTeam_pdg') return false;
                        return true;
                      },
                    },
                  )}
                />
                {hasBreakdown
                  ? breakdownKeys.map((key, i) => {
                      const color = getBreakdownColor(key, team);
                      // 内訳ビューは要件定義pt 棒が最上段に積まれ、合計ラベルもそちらに付く
                      const isTop = !isBreakdownView && i === breakdownKeys.length - 1;
                      return (
                        <Bar
                          key={key}
                          dataKey={`byTeam_${key}`}
                          name={`byTeam_${key}`}
                          stackId="actual"
                          fill={color}
                          fillOpacity={0.85}
                          stroke={color}
                          strokeWidth={1}
                          maxBarSize={BAR_MAX_SIZE}
                          radius={stackedBarRadius(
                            i,
                            breakdownKeys.length + (isBreakdownView ? 1 : 0),
                          )}
                          onClick={onWeekClick ? (handleBarClick as never) : undefined}
                          style={onWeekClick ? { cursor: 'pointer' } : undefined}
                        >
                          {isTop && (
                            <LabelList
                              dataKey="actualWithWip"
                              position="top"
                              fill="var(--color-text-primary)"
                              fontSize={11}
                              formatter={
                                ((value: number) =>
                                  value > 0 ? Math.round(value).toLocaleString() : '') as never
                              }
                            />
                          )}
                        </Bar>
                      );
                    })
                  : (() => {
                      // 親 team が代表色を持つ leaf team (aircloset/platform/mall/qa/tet/sre) の
                      // 場合はその色で描画。API が byTeam=null を返すため単色 Bar 経路に入る
                      // TE サブチーム (growth/experience) は内訳バケットを持たないので、
                      // 親である tet の色で描く（TE の一部であることを色でも示す）
                      const colorKey =
                        team && isTetSubTeam(team)
                          ? 'tet'
                          : team && team !== 'all'
                            ? team
                            : undefined;
                      const leafColor = colorKey ? BREAKDOWN_COLORS[colorKey] : undefined;
                      const fill = leafColor ?? 'var(--color-success)';
                      return (
                        <Bar
                          dataKey="actualWithWip"
                          name="actualWithWip"
                          stackId="actual"
                          fill={fill}
                          fillOpacity={leafColor ? 0.85 : 0.7}
                          stroke={fill}
                          strokeWidth={1}
                          maxBarSize={BAR_MAX_SIZE}
                          radius={isBreakdownView ? undefined : BAR_RADIUS_TOP}
                          onClick={onWeekClick ? (handleBarClick as never) : undefined}
                          style={onWeekClick ? { cursor: 'pointer' } : undefined}
                        >
                          {!isBreakdownView && (
                            <LabelList
                              dataKey="actualWithWip"
                              position="top"
                              fill="var(--color-text-primary)"
                              fontSize={11}
                              formatter={
                                ((value: number) =>
                                  value > 0 ? Math.round(value).toLocaleString() : '') as never
                              }
                            />
                          )}
                        </Bar>
                      );
                    })()}
                {isBreakdownView &&
                  renderRequirementSegment(onWeekClick ? handleBarClick : undefined)}
                {renderWeeklyTargetLines(effectiveView, onWeekClick ? handleBarClick : undefined)}
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="h-56 flex items-center justify-center border border-dashed border-[var(--color-border)] rounded-lg">
            <p className="text-[var(--color-text-muted)]">{t('status.noData')}</p>
          </div>
        )}
        {!isLoading && data.length > 0 && (
          <WeeklyPointTable
            data={data}
            onWeekClick={onWeekClick}
            team={team}
            view={effectiveView}
          />
        )}
      </CardContent>
    </Card>
  );
}
